/*
 * VH-209 — Which ingredients does a step mention, and in what amount?
 *
 * Recipe steps are free text (`recipe_steps.text`) with no link to the
 * ingredient rows, so the association is recovered by matching names in the
 * text. Nothing is stored: the annotation is computed from the version being
 * rendered, which is what makes it correct for recipes that already exist and
 * for a scaled version (the scale flow writes a NEW version with new amounts —
 * we read whatever is in front of us).
 *
 * The system holds ONE amount per ingredient, not a per-step split. An
 * ingredient named in two steps therefore reports its full amount in both,
 * flagged `isTotal` so the UI can say so rather than let it read as "200 g,
 * twice".
 *
 * Matching is deliberately conservative. A missed mention costs the user a
 * glance at the ingredient list — which is where they are today. A WRONG
 * mention puts a number next to the wrong food while they cook.
 */

/** Fold case, strip diacritics, and reduce punctuation to spaces. */
function normalize(value) {
  return (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

/*
 * Suffixes trimmed from a name before matching, so "onions" in a step finds
 * the "onion" row and "Zwiebeln" finds "Zwiebel". Longest first — trimming
 * "n" before "en" would leave a stem that no longer matches.
 *
 * Stripping runs to a FIXED POINT rather than once, so both sides of a
 * comparison converge on the same stem: one pass turns "onions" into "onion"
 * but "onion" into "onio", and the two would then never match. Repeating until
 * nothing more comes off makes both "onio".
 *
 * Never shortens below 3 characters: "oils" -> "oil" is right, but cutting a
 * two-letter stem invites collisions with ordinary words.
 */
const SUFFIXES = ['es', 'en', 'er', 's', 'n', 'e']
const MIN_STEM = 3

function stem(word) {
  let current = word
  let changed = true
  while (changed) {
    changed = false
    for (const suffix of SUFFIXES) {
      if (
        current.length - suffix.length >= MIN_STEM &&
        current.endsWith(suffix)
      ) {
        current = current.slice(0, -suffix.length)
        changed = true
        break
      }
    }
  }
  return current
}

/** Normalize, then stem each word, so "olive oils" ~ "olive oil". */
function key(value) {
  const norm = normalize(value)
  if (!norm) return ''
  return norm.split(' ').map(stem).join(' ')
}

/*
 * Does `haystack` contain `needle` as whole words? Both arrive already
 * normalized and stemmed, so this is a word-boundary substring test — which is
 * what keeps "egg" out of "eggplant" while still matching "olive oil" as a
 * phrase.
 */
function containsPhrase(haystack, needle) {
  if (!needle) return false
  const hay = ` ${haystack} `
  return hay.includes(` ${needle} `)
}

/*
 * Steps that refer to the ingredients COLLECTIVELY rather than by name:
 * "mix all ingredients", "alle Zutaten verrühren", "misture todos os
 * ingredientes". Measured against the real recipe book, this is not a corner
 * case — it is how most bread recipes are written, and a name matcher finds
 * nothing in them at all.
 *
 * These run AFTER normalization and stemming, so they must be written against
 * stems, not against the words a cook would type: "Alle" stems to "all",
 * "Zutaten" to "zutat", "todos" to "todo", "ingredients" to "ingredient".
 * A hit annotates the step with EVERY ingredient, which is exactly what the
 * step is telling the cook to reach for.
 */
const ALL_INGREDIENTS_PATTERNS = [
  // "mix all ingredients", "all the ingredients for the main dough", and —
  // since German "Alle" also stems to "all" — "alle Zutaten".
  /\ball\b.{0,30}\bingredient\b/,
  /\ball\b.{0,30}\bzutat\b/,
  /\btodo\b.{0,30}\bingredient\b/,
]

function mentionsAllIngredients(stemmedText) {
  return ALL_INGREDIENTS_PATTERNS.some((re) => re.test(stemmedText))
}

/**
 * Build the per-step ingredient annotation for one recipe version.
 *
 * @param {Array} steps        - the version's steps, in display order
 * @param {Array} ingredients  - the version's ingredients, in display order
 * @param {Object} [options]
 * @param {Object} [options.translatedSteps]       - step id -> translated text
 * @param {Object} [options.translatedIngredients] - ingredient id -> translated name
 *
 * Both translation maps are the ones the detail page already resolves. Passing
 * them keeps matching inside ONE language: a German step is matched against
 * German ingredient names, and the German name is what gets displayed.
 *
 * @returns {Object} step id -> array of
 *   { id, name, amount, unit, notes, isTotal }
 *   in the recipe's ingredient order. Steps that mention nothing are absent.
 */
export function buildStepIngredients(steps, ingredients, options = {}) {
  const { translatedSteps, translatedIngredients } = options
  const result = {}

  const stepList = Array.isArray(steps) ? steps : []
  const ingredientList = Array.isArray(ingredients) ? ingredients : []
  if (!stepList.length || !ingredientList.length) return result

  // Display name and match keys per ingredient. The snapshot name is kept as a
  // second key so an untranslated step still matches a translated recipe.
  const prepared = ingredientList.map((ing) => {
    const displayName =
      translatedIngredients?.[ing.id] || ing.food_name_snapshot || ''
    const keys = [key(displayName), key(ing.food_name_snapshot)].filter(
      (k, idx, all) => k && all.indexOf(k) === idx
    )
    return { ing, displayName, keys }
  })

  /*
   * Recipes name an ingredient by its head noun: the row is "Dark Chocolate"
   * and the step says "melt the chocolate". So the last word of a multi-word
   * name is also a key — but ONLY when no other ingredient ends in the same
   * word. With both olive oil and sunflower oil in the list, a bare "oil"
   * genuinely is ambiguous, and guessing would put a number beside the wrong
   * food. Short words are excluded; they collide with ordinary prose.
   */
  const LAST_WORD_MIN = 4
  const lastWordCounts = new Map()
  for (const p of prepared) {
    for (const k of new Set(p.keys.map((k) => k.split(' ').slice(-1)[0]))) {
      lastWordCounts.set(k, (lastWordCounts.get(k) || 0) + 1)
    }
  }
  for (const p of prepared) {
    for (const k of p.keys) {
      const words = k.split(' ')
      if (words.length < 2) continue
      const last = words[words.length - 1]
      if (
        last.length >= LAST_WORD_MIN &&
        lastWordCounts.get(last) === 1 &&
        !p.keys.includes(last)
      ) {
        p.keys.push(last)
      }
    }
  }

  // First pass: who mentions whom. Counting mentions before emitting anything
  // is what lets a repeat be marked as a total on its FIRST occurrence too.
  const mentions = new Map() // ingredient id -> count of steps mentioning it
  const perStep = new Map() // step id -> prepared[] in ingredient order

  for (const step of stepList) {
    const text = key(translatedSteps?.[step.id] || step.text || '')
    if (!text) continue
    const hits = mentionsAllIngredients(text)
      ? prepared
      : prepared.filter((p) => p.keys.some((k) => containsPhrase(text, k)))
    if (!hits.length) continue
    perStep.set(step.id, hits)
    for (const hit of hits) {
      mentions.set(hit.ing.id, (mentions.get(hit.ing.id) || 0) + 1)
    }
  }

  for (const [stepId, hits] of perStep) {
    result[stepId] = hits.map(({ ing, displayName }) => ({
      id: ing.id,
      name: displayName,
      amount: ing.amount,
      unit: ing.unit,
      notes: ing.notes || null,
      isTotal: (mentions.get(ing.id) || 0) > 1,
    }))
  }

  return result
}

export default buildStepIngredients
